<template>
    <common-modal
        :heading="$t('modals.patient_profile.profile.detail_form.share_modal.heading')"
        width="medium"
        height="medium"
        @close="$emit('close')"
    >
        <div class="share-modal-body">
            <div class="selected-documents-section">
                <p class="modal-subtitle">
                    {{
                        $t('modals.patient_profile.profile.detail_form.share_modal.subtitle', { count: selectedDocuments.length })
                    }}
                </p>
                <div class="selected-documents-list">
                    <span v-for="doc in selectedDocuments" :key="doc.id" class="doc-tag" :title="doc.title || doc.fileName">
                        {{ doc.title || doc.fileName }}
                    </span>
                </div>
            </div>

            <label class="form-field__label">
                {{ $t('modals.patient_profile.profile.detail_form.share_modal.recipient_label') }}
                <span class="required-star">*</span>
            </label>
            <div v-clickaway="closeDropdown" class="recipient-select-wrapper">
                <div class="recipient-input-container">
                    <input
                        ref="recipientInput"
                        v-model="recipientSearch"
                        type="text"
                        class="search-input"
                        :placeholder="$t('modals.patient_profile.profile.detail_form.share_modal.recipient_placeholder')"
                        autocomplete="off"
                        @focus="openDropdown"
                        @click="openDropdown"
                        @input="onSearchInput"
                        @keydown.down.prevent="highlightNext"
                        @keydown.up.prevent="highlightPrev"
                        @keydown.enter.prevent="selectHighlighted"
                        @keydown.esc.prevent="closeDropdown"
                    />
                    <div class="input-actions">
                        <button
                            v-if="selectedRecipientType || recipientSearch"
                            type="button"
                            class="icon-btn clear-btn"
                            :title="$t('modals.patient_profile.profile.detail_form.share_modal.clear_selection')"
                            @click="clearRecipient"
                        >
                            <common-icon name="close" size="tiny"></common-icon>
                        </button>
                        <button type="button" :class="arrowButtonClass" @click="toggleDropdown">
                            <common-icon name="dropdown" size="small"></common-icon>
                        </button>
                    </div>
                </div>

                <div v-if="isDropdownOpen" class="recipient-dropdown-menu">
                    <div
                        v-for="(option, index) in filteredRecipientOptions"
                        :key="option.value"
                        :class="recipientOptionClass(option, index)"
                        @mouseenter="highlightedIndex = index"
                        @mousedown.prevent="selectRecipient(option)"
                        @click="selectRecipient(option)"
                    >
                        <span>{{ option.label }}</span>
                        <common-icon v-if="option.value === selectedRecipientType" name="check" size="tiny"></common-icon>
                    </div>
                    <div v-if="filteredRecipientOptions.length === 0" class="recipient-dropdown-empty">
                        {{ $t('modals.patient_profile.profile.detail_form.share_modal.no_recipients_found') }}
                    </div>
                </div>
            </div>

            <label class="form-field__label">
                {{ $t('modals.patient_profile.profile.detail_form.share_modal.email_label') }}
            </label>
            <input
                v-model.trim="recipientEmail"
                type="email"
                class="search-input email-input"
                :placeholder="$t('modals.patient_profile.profile.detail_form.share_modal.email_placeholder')"
            />

            <div v-if="shareLink" class="share-link-box">
                <input type="text" class="search-input" readonly :value="shareLink" />
            </div>

            <div v-if="accessCode" class="share-link-box access-code-box">
                <label class="form-field__label">
                    {{ $t('modals.patient_profile.profile.detail_form.share_modal.access_code_label') }}
                </label>
                <div class="access-code-row">
                    <input type="text" class="search-input" readonly :value="accessCode" />
                    <common-button
                        size="small"
                        type="secondary"
                        :label="$t('modals.patient_profile.profile.detail_form.share_modal.copy_access_code')"
                        @click="copyAccessCode"
                    ></common-button>
                </div>
            </div>

            <div class="share-modal-footer">
                <common-buttons align="right">
                    <common-button
                        size="small"
                        :isSubmittable="false"
                        :isProcessing="isCreatingLink"
                        :isDisabled="!canCreateLink"
                        :label="
                            shareLink
                                ? $t('modals.patient_profile.profile.detail_form.share_modal.copy_link')
                                : $t('modals.patient_profile.profile.detail_form.share_modal.create_link')
                        "
                        type="secondary"
                        @click="shareLink ? copyShareLink() : createShareLink()"
                    ></common-button>
                    <common-button
                        size="small"
                        :isSubmittable="false"
                        :isProcessing="isSharing"
                        :isDisabled="!canSendEmail"
                        :label="$t('modals.patient_profile.profile.detail_form.share_modal.send')"
                        type="primary"
                        @click="sendShareEmail"
                    ></common-button>
                </common-buttons>

                <p v-if="shareFeedbackMessage" class="modal-subtitle feedback-msg">
                    {{ shareFeedbackMessage }}
                </p>
            </div>
        </div>
    </common-modal>
</template>

<script>
    import PatientAPI from 'api/PatientAPI'
    import { RECORD_SHARE_RECIPIENT_OPTIONS } from '@/enums/RecordShareRecipientTypeEnum'

    export default {
        props: {
            selectedDocuments: {
                type: Array,
                default: () => [],
            },
            patientId: {
                type: [Number, String],
                required: true,
            },
        },

        data() {
            return {
                selectedRecipientType: '',
                recipientSearch: '',
                isDropdownOpen: false,
                highlightedIndex: 0,
                recipientEmail: '',
                shareLink: '',
                accessCode: '',
                shareFeedbackMessage: '',
                isCreatingLink: false,
                isSharing: false,
            }
        },

        computed: {
            recipientOptions() {
                return RECORD_SHARE_RECIPIENT_OPTIONS
            },

            filteredRecipientOptions() {
                const search = (this.recipientSearch || '').trim().toLowerCase()
                if (!search) {
                    return this.recipientOptions
                }

                if (this.selectedRecipientType && this.recipientSearch === this.selectedOptionLabel) {
                    return this.recipientOptions
                }

                return this.recipientOptions.filter((opt) => opt.label.toLowerCase().includes(search))
            },

            selectedOptionLabel() {
                const found = this.recipientOptions.find((opt) => opt.value === this.selectedRecipientType)
                return found ? found.label : ''
            },

            isEmailValid() {
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((this.recipientEmail || '').trim())
            },

            canCreateLink() {
                return !this.isCreatingLink && !this.isSharing && !!this.selectedRecipientType
            },

            canSendEmail() {
                return !this.isSharing && !this.isCreatingLink && !!this.selectedRecipientType && this.isEmailValid
            },

            arrowButtonClass() {
                return {
                    'icon-btn': true,
                    'arrow-btn': true,
                    'is-open': this.isDropdownOpen,
                }
            },

            recipientOptionClass() {
                return (option, index) => ({
                    'recipient-dropdown-item': true,
                    'is-highlighted': index === this.highlightedIndex,
                    'is-selected': option.value === this.selectedRecipientType,
                })
            },
        },

        methods: {
            openDropdown() {
                this.isDropdownOpen = true
                this.highlightedIndex = 0
            },

            closeDropdown() {
                this.isDropdownOpen = false
                if (this.selectedRecipientType) {
                    this.recipientSearch = this.selectedOptionLabel
                } else if (
                    !this.recipientOptions.some((o) => o.label.toLowerCase() === this.recipientSearch.trim().toLowerCase())
                ) {
                    this.recipientSearch = ''
                }
            },

            toggleDropdown() {
                if (this.isDropdownOpen) {
                    this.closeDropdown()
                } else {
                    this.openDropdown()
                    this.$refs.recipientInput && this.$refs.recipientInput.focus()
                }
            },

            onSearchInput() {
                this.isDropdownOpen = true
                this.highlightedIndex = 0

                if (this.selectedRecipientType && this.recipientSearch !== this.selectedOptionLabel) {
                    this.selectedRecipientType = ''
                }

                const exactMatch = this.recipientOptions.find(
                    (opt) => opt.label.toLowerCase() === this.recipientSearch.trim().toLowerCase(),
                )
                if (exactMatch) {
                    this.selectedRecipientType = exactMatch.value
                }
            },

            selectRecipient(option) {
                this.selectedRecipientType = option.value
                this.recipientSearch = option.label
                this.isDropdownOpen = false
            },

            clearRecipient() {
                this.selectedRecipientType = ''
                this.recipientSearch = ''
                this.isDropdownOpen = false
                this.$refs.recipientInput && this.$refs.recipientInput.focus()
            },

            highlightNext() {
                if (!this.isDropdownOpen) {
                    this.openDropdown()
                    return
                }
                if (this.filteredRecipientOptions.length > 0) {
                    this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredRecipientOptions.length
                }
            },

            highlightPrev() {
                if (!this.isDropdownOpen) {
                    this.openDropdown()
                    return
                }
                if (this.filteredRecipientOptions.length > 0) {
                    this.highlightedIndex =
                        (this.highlightedIndex - 1 + this.filteredRecipientOptions.length) % this.filteredRecipientOptions.length
                }
            },

            selectHighlighted() {
                if (this.isDropdownOpen && this.filteredRecipientOptions.length > 0) {
                    const option = this.filteredRecipientOptions[this.highlightedIndex]
                    if (option) {
                        this.selectRecipient(option)
                    }
                }
            },

            createShareLink() {
                if (!this.canCreateLink) return

                this.isCreatingLink = true
                return this.requestShare()
                    .then((res) => {
                        this.shareLink = res.link
                        this.accessCode = res.accessCode || ''
                        this.shareFeedbackMessage = this.$t('modals.patient_profile.profile.detail_form.share_modal.created')
                        this.$emit('shared', res)
                    })
                    .finally(() => {
                        this.isCreatingLink = false
                    })
            },

            sendShareEmail() {
                if (!this.canSendEmail) return

                this.isSharing = true
                return this.requestShare(this.recipientEmail.trim())
                    .then((res) => {
                        this.shareLink = res.link
                        this.accessCode = res.accessCode || ''
                        this.shareFeedbackMessage = res.emailSent
                            ? this.$t('modals.patient_profile.profile.detail_form.share_modal.sent', {
                                  email: this.recipientEmail.trim(),
                              })
                            : this.$t('modals.patient_profile.profile.detail_form.share_modal.email_failed')
                        this.$emit('shared', res)
                    })
                    .finally(() => {
                        this.isSharing = false
                    })
            },

            requestShare(recipientEmail) {
                return PatientAPI.shareDocuments({
                    patientId: Number(this.patientId),
                    documentIds: this.selectedDocuments.map((d) => d.id),
                    recipientEmail,
                    recipientType: this.selectedRecipientType,
                })
            },

            copyShareLink() {
                navigator.clipboard.writeText(this.shareLink).then(() => {
                    this.shareFeedbackMessage = this.$t('modals.patient_profile.profile.detail_form.share_modal.copied')
                })
            },

            copyAccessCode() {
                navigator.clipboard.writeText(this.accessCode).then(() => {
                    this.shareFeedbackMessage = this.$t(
                        'modals.patient_profile.profile.detail_form.share_modal.access_code_copied',
                    )
                })
            },
        },
    }
</script>

<style scoped lang="scss">
    .share-modal-body {
        padding: 8px 0 4px;
    }

    .selected-documents-section {
        margin-bottom: 16px;
        min-height: 160px;
        .modal-subtitle {
            margin-bottom: 6px;
        }
    }

    .selected-documents-list {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        max-height: 130px;
        overflow-y: auto;
        padding-right: 4px;
    }

    .doc-tag {
        display: inline-block;
        background: #f3f4f6;
        color: #374151;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        padding: 3px 8px;
        font-size: 12px;
        max-width: 260px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .modal-subtitle {
        font-size: 14px;
        color: #666;
        margin-bottom: 15px;
        line-height: 1.4;

        &.feedback-msg {
            margin-top: 10px;
            margin-bottom: 0;
            text-align: right;
            font-weight: 500;
            color: #2e8b57;
        }
    }

    .form-field__label {
        display: block;
        font-size: 13px;
        font-weight: 500;
        color: #4b5563;
        margin-bottom: 6px;

        .required-star {
            color: #e53935;
            margin-left: 2px;
        }
    }

    .recipient-select-wrapper {
        position: relative;
        margin-bottom: 16px;
    }

    .recipient-input-container {
        position: relative;
        display: flex;
        align-items: center;
    }

    .search-input {
        width: 100%;
        padding: 9px 58px 9px 12px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
        color: #1f2937;
        background-color: #fff;
        box-sizing: border-box;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &:focus {
            outline: none;
            border-color: #2e8b57;
            box-shadow: 0 0 0 2px rgba(46, 139, 87, 0.15);
        }

        &::placeholder {
            color: #9ca3af;
        }
    }

    .email-input {
        padding-right: 12px;
        margin-bottom: 16px;
    }

    .input-actions {
        position: absolute;
        right: 8px;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .icon-btn {
        background: transparent;
        border: none;
        padding: 4px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6b7280;
        border-radius: 4px;
        transition: background-color 0.15s ease, color 0.15s ease;

        &:hover {
            background-color: #f3f4f6;
            color: #111827;
        }

        &.arrow-btn {
            transition: transform 0.2s ease;

            &.is-open {
                transform: rotate(180deg);
            }
        }
    }

    .recipient-dropdown-menu {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        max-height: 160px;
        overflow-y: auto;
        z-index: 1050;
        padding: 4px 0;
    }

    .recipient-dropdown-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 14px;
        font-size: 13.5px;
        color: #374151;
        cursor: pointer;
        transition: background-color 0.12s ease, color 0.12s ease;

        &:hover,
        &.is-highlighted {
            background-color: #f0f7f4;
            color: #2e8b57;
        }

        &.is-selected {
            background-color: #e6f4ed;
            color: #2e8b57;
            font-weight: 600;
        }
    }

    .recipient-dropdown-empty {
        padding: 12px 14px;
        font-size: 13px;
        color: #9ca3af;
        text-align: center;
    }

    .share-link-box {
        margin-bottom: 16px;

        input {
            background-color: #f9fafb;
            color: #4b5563;
            cursor: text;
            padding-right: 12px;
        }
    }

    .access-code-row {
        display: flex;
        align-items: center;
        gap: 8px;

        input {
            flex: 1;
            letter-spacing: 2px;
            font-weight: 600;
        }
    }

    .share-modal-footer {
        border-top: 1px solid #eee;
        padding-top: 16px;
        margin-top: 20px;
    }
</style>
